import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import PageHeader from "@/components/PageHeader";
import Card from "@/components/Card";
import EntityDetailDrawer from "@/components/EntityDetailDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = { name: "", category: "", contact_name: "", email: "", phone: "" };

export default function Vendors() {
  const { fmtMoney } = useCurrency();
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [drawerId, setDrawerId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = () => api.get("/vendors").then((r) => setVendors(r.data || []));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditItem(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (v, e) => {
    e.stopPropagation();
    setEditItem(v);
    setForm({ name: v.name, category: v.category || "", contact_name: v.contact_name || "", email: v.email || "", phone: v.phone || "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name) return toast.error("Name is required");
    if (editItem) {
      await api.put(`/vendors/${editItem.id}`, { ...editItem, ...form });
      toast.success("Vendor updated");
    } else {
      await api.post("/vendors", form);
      toast.success("Vendor added");
    }
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/vendors/${id}`);
    toast.success("Vendor removed");
    load();
  };

  return (
    <div data-testid="vendors-page" className="max-w-[1400px] mx-auto">
      <PageHeader
        title="Vendors"
        subtitle={`${vendors.length} suppliers · ${fmtMoney(vendors.reduce((s, v) => s + (v.total_spend || 0), 0))} total spend`}
        action={
          <Button data-testid="add-vendor-btn" onClick={openCreate} className="rounded-full bg-black text-white hover:bg-black/85">
            <Plus className="w-4 h-4 mr-1.5" /> Add vendor
          </Button>
        }
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditItem(null); }}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle>{editItem ? "Edit vendor" : "New vendor"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            {[["name", "Name"], ["category", "Category"], ["contact_name", "Contact person"], ["email", "Email"], ["phone", "Phone"]].map(([k, l]) => (
              <div key={k} className="grid gap-1.5">
                <Label className="text-[12px]">{l}</Label>
                <Input data-testid={`vendor-input-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-xl" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="save-vendor-btn" onClick={submit} className="rounded-full">
              {editItem ? "Save changes" : "Save vendor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vendors.map((v) => (
            <button
              type="button"
              key={v.id}
              data-testid={`vendor-${v.id}`}
              onClick={() => setDrawerId(v.id)}
              className="text-left p-4 rounded-2xl bg-white border border-black/[0.04] card-hover"
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F5F5F7] to-[#E5E5EA] flex items-center justify-center font-display font-semibold text-[15px]">
                  {v.name.charAt(0)}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={(e) => openEdit(v, e)} className="text-[#8E8E93] hover:text-[#1C1C1E] transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); setConfirmId(v.id); }} className="text-[#8E8E93] hover:text-[#FF3B30] transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-3 font-display font-semibold text-[15px]">{v.name}</div>
              <div className="text-[11.5px] text-[#8E8E93]">{v.category || "General"}</div>
              <div className="mt-3 text-[12px] text-[#3C3C43]/80">{v.contact_name} · {v.email}</div>
              <div className="mt-3 pt-3 border-t border-black/[0.04] flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-[#8E8E93]">Total spend</span>
                <span className="font-mono font-semibold">{fmtMoney(v.total_spend)}</span>
              </div>
            </button>
          ))}
          {vendors.length === 0 && <div className="col-span-full py-8 text-center text-[#8E8E93]">No vendors yet.</div>}
        </div>
      </Card>
      <EntityDetailDrawer
        entityType="vendor"
        entityId={drawerId}
        open={!!drawerId}
        onOpenChange={(v) => !v && setDrawerId(null)}
      />

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove vendor?</AlertDialogTitle>
            <AlertDialogDescription>This vendor will be permanently removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              onClick={() => { remove(confirmId); setConfirmId(null); }}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
